import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import type { AgentStatus } from '@/api/types';

const statusColor: Record<AgentStatus, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-400',
  busy: 'bg-red-500',
  offline: 'bg-slate-300',
};

export function StatusDot({ status, className }: { status: AgentStatus; className?: string }) {
  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 rounded-full', statusColor[status], className)}
      aria-hidden
    />
  );
}

// Each person keeps a stable colour, so lists are easier to scan than a wall of identical circles.
const PALETTES = [
  'from-violet-500 to-fuchsia-500',
  'from-sky-500 to-indigo-500',
  'from-emerald-500 to-teal-500',
  'from-amber-500 to-orange-500',
  'from-rose-500 to-pink-500',
  'from-indigo-500 to-violet-600',
  'from-cyan-500 to-sky-600',
  'from-fuchsia-500 to-rose-500',
];

function paletteFor(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTES[h % PALETTES.length]!;
}

export function Avatar({
  name,
  src,
  status,
  size = 'md',
  className,
}: {
  name: string;
  src?: string | null;
  status?: AgentStatus;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const dims = {
    xs: 'h-5 w-5 text-[9px]',
    sm: 'h-7 w-7 text-[11px]',
    md: 'h-9 w-9 text-xs',
    lg: 'h-14 w-14 text-base',
    xl: 'h-20 w-20 text-xl',
  }[size];
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {src ? (
        <img src={src} alt="" className={cn('rounded-full object-cover', dims)} />
      ) : (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-gradient-to-br font-semibold text-white ring-1 ring-black/5',
            paletteFor(name),
            dims,
          )}
          aria-hidden
        >
          {initials(name)}
        </span>
      )}
      {status && <StatusDot status={status} className="absolute -right-0.5 -bottom-0.5 ring-2 ring-white" />}
    </span>
  );
}
